import React, {useState, useEffect, useCallback} from "react";
import StoryCard from "./StoryCard";
import {
  handleAssetClick,
  calculateAverageEmbedding,
  getStoredEmbeddings,
  STORED_ASSETS_KEY,
  USER_EMBEDDING_META_KEY,
} from "../utils";
import {useNavigate} from "react-router-dom";
import Notification from "./Notification";
import {useStories} from "../hooks/useStories";
import {getNeuronItemId} from "../utils/getNeuronItemId";
import {useDebugMode} from "../hooks/useDebugMode";
import SectionHeading from "./SectionHeading";
import {Skeleton} from "./ui/skeleton";

const TopStories = ({selectedCategory, viewInterceptor}) => {
  const debugMode = useDebugMode();
  const [randomizedStories, setRandomizedStories] = useState([]);
  const [notification, setNotification] = useState("");
  const navigate = useNavigate();

  const {stories, loading: storiesLoading} = useStories();

  useEffect(() => {
    const handleEmbeddingUpdated = (event) => {
      if (event.detail && event.detail.message) {
        setNotification(event.detail.message);
        updateStoredAssetsInfo();
      }
    };

    window.addEventListener("embeddingUpdated", handleEmbeddingUpdated);
    return () => {
      window.removeEventListener("embeddingUpdated", handleEmbeddingUpdated);
    };
  }, []);

  const randomizeStories = useCallback(() => {
    const filteredStories = selectedCategory
      ? stories.filter((story) => story.aiCategorization === selectedCategory)
      : stories;

    const shuffledStories = [...filteredStories].sort(
      () => Math.random() - 0.5
    );
    setRandomizedStories(shuffledStories);
  }, [selectedCategory, stories]);

  useEffect(() => {
    if (!storiesLoading) {
      randomizeStories();
    }
  }, [randomizeStories, storiesLoading]);

  const updateStoredAssetsInfo = () => {
    const storedAssets = getStoredEmbeddings();
    const count = storedAssets.length;
    if (count > 0) {
      calculateAverageEmbedding(storedAssets);
    }

    setNotification(`Assets clicked: ${count}.`);
  };

  const resetStoredAssets = () => {
    localStorage.removeItem(STORED_ASSETS_KEY);
    localStorage.removeItem(USER_EMBEDDING_META_KEY);
    setNotification("Stored assets have been reset.");
  };

  const NOTE = "Not personalised. The control, for comparison.";
  const DEBUG_NOTE = "not an NSL surface · random shuffle of local StoriesData";

  if (storiesLoading) {
    return (
      <section className="container py-8">
        <SectionHeading kicker="Control" title="Discover" />
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1.75fr)_minmax(0,1fr)]">
          <div className="flex flex-col gap-3">
            <Skeleton className="aspect-[16/9] w-full" />
            <Skeleton className="h-9 w-4/5" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
          <div className="flex flex-col gap-5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex gap-3">
                <div className="flex flex-1 flex-col gap-2">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-5 w-full" />
                  <Skeleton className="h-5 w-3/4" />
                </div>
                <Skeleton className="size-[84px] shrink-0" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  const [lead, ...rest] = randomizedStories.slice(0, 5);

  const toCard = (story, index, variant) => (
    <StoryCard
      key={story.uuid || index}
      id={story.uuid}
      ref={viewInterceptor?.getItemRef(getNeuronItemId(story))}
      title={story.aiTitle}
      description={story.aiSummary}
      image={story.image}
      category={story.aiCategorization}
      duration={story.timeToRead}
      embeddingValue={story.aiEmbeddingValue}
      variant={variant}
      onAssetClick={(embeddingValue) =>
        handleAssetClick(embeddingValue, () => navigate(`/article/${story.uuid}`))
      }
      sources={[story.source]}
    />
  );

  return (
    <section className="container py-8">
      <SectionHeading
        kicker="Control"
        title="Discover"
        standfirst={NOTE}
        tone="editorial"
        debugNote={DEBUG_NOTE}
        debugMode={debugMode}
      />

      {/* Broadsheet asymmetry: one story owns the fold, the rest report in. */}
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1.75fr)_minmax(0,1fr)] lg:gap-10">
        {lead ? toCard(lead, 0, "lead") : null}

        {rest.length ? (
          <div className="flex flex-col divide-y divide-rule border-t border-rule lg:border-t-0 lg:pt-0">
            {rest.map((story, index) => (
              <div key={story.uuid || index} className="py-4 first:pt-0 lg:first:pt-0">
                {toCard(story, index + 1, "compact")}
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <Notification
        message={notification}
        onReset={resetStoredAssets}
        onClose={() => setNotification("")}
      />
    </section>
  );
};

export default TopStories;
